import { ChevronIcon, Paragraph } from '@flysas-tech/react-components';
import { NavLink, useLocation } from '@remix-run/react';
import clsx from 'clsx';
import { useState } from 'react';

/**
 * Switcher component.
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The children components.
 * @param {boolean} props.expanded - Shows if the switcher is expanded or not.
 * @param {{ route: string, logo: string, title: string }} props.selected - The selected link object.
 * @param {Function} props.setExpanded - The function to set the expanded state.
 * @param {import('react').ReactElement<{className: string}>} props.logo - The logo component.
 * @returns {import('react').ReactElement<Props>} - The Switcher component.
 */
export function Switcher({ children, expanded, logo, selected, setExpanded }) {
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState();
  const { pathname } = useLocation();

  if (!expanded && isSwitcherOpen) {
    setIsSwitcherOpen(false);
  }

  if (pathname !== currentLocation) {
    setCurrentLocation(pathname);
    setIsSwitcherOpen(false);
  }

  function toggleSwitcher() {
    setIsSwitcherOpen(!isSwitcherOpen);
  }
  return (
    <div className="flex flex-col gap-1 overflow-hidden">
      <SwitcherOption
        link={selected}
        logo={logo}
        onClickOpenSwitcher={setExpanded}
        expanded={expanded}
        onClickChangeSelected={toggleSwitcher}
        navigate={false}
      >
        <ChevronIcon
          color="grey-10"
          rotation={isSwitcherOpen ? '180' : '0'}
          className="mr-6 cursor-pointer"
          onClick={toggleSwitcher}
        />
      </SwitcherOption>
      <div
        className={`absolute top-[8rem] flex flex-col gap-1 bg-white transition-all duration-300 ease-in-out overflow-hidden ${
          isSwitcherOpen ? 'opacity-100' : 'max-h-0 opacity-0'
        } flex-1 w-[calc(100%-1rem)]`}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * SwitcherOption component.
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The children components.
 * @param {boolean} props.expanded - Shows if the switcher is expanded or not.
 * @param {{ route: string, icon: string, title: string }} props.link - The selected link object.
 * @param {Function} props.onClickChangeSelected - The function to change the selected link.
 * @param {Function} props.onClickOpenSwitcher - The function to open the switcher.
 * @param {import('react').ReactElement<{className: string}>} props.logo - The logo component.
 * @param {boolean} [props.navigate] - Whether to navigate or not. Defaults to true
 * @returns {import('react').ReactElement<Props>} - The SwitcherOption component.
 */
export function SwitcherOption({
  children,
  expanded,
  link,
  logo: Logo,
  navigate = true,
  onClickChangeSelected,
  onClickOpenSwitcher,
}) {
  function switchSelected() {
    onClickChangeSelected(link);
  }

  return (
    <div className="flex flex-1">
      <span onClick={onClickOpenSwitcher}>
        <Logo className={`${expanded ? 'rounded-l' : 'rounded'} overflow-hidden cursor-pointer`} />
      </span>
      <div className="flex items-center h-12 w-44 rounded-r justify-between no-underline bg-turquoise-90 flex-1">
        <SwitcherOptionClickTarget to={link.route} onClick={switchSelected} className="flex-1 py-4" navigate={navigate}>
          <Paragraph
            variant="tertiary"
            weight="bold"
            name={link.title}
            className={`pl-4 text-blue-30 transition-opacity duration-300 ease-in-out whitespace-nowrap ${
              expanded ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {link.displayName || link.title}
          </Paragraph>
        </SwitcherOptionClickTarget>
        {children}
      </div>
    </div>
  );
}

/**
 * small component to handle showing the right semantic element based on the navigate prop
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The children components.
 * @param {string} props.className - The class name.
 * @param {boolean} props.navigate - Whether to navigate or not.
 * @param {Function} props.onClick - Click handler.
 * @param {string} props.to - The route to navigate to.
 * @returns {import('react').ReactElement<Props>} - The SwitcherOptionClickTarget component.
 */
function SwitcherOptionClickTarget({ children, className, navigate, onClick, to, ...rest }) {
  if (navigate) {
    return (
      <NavLink to={to} onClick={onClick} prefetch="viewport" viewTransition className={className} {...rest}>
        {children}
      </NavLink>
    );
  }
  return (
    <button onClick={onClick} className={clsx('text-left', className)} {...rest}>
      {children}
    </button>
  );
}
